import { useContext, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Chip,useTheme } from '@mui/material';
import { AppVarsContext } from '../../../context/AppVarsContext';
import {
  getExecutiveGlassAutocompleteSx,
  getExecutiveGlassPopperSx,
} from '../../../views/shared/reportSurfaceStyles';

const SERVICE_ENDPOINTS = {
  meta: '/meta/conversions',
  google: '/adwords/conversions',
  adwords: '/adwords/conversions',
};

const normalizeSelectValue = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

const pushUniqueValue = (list, value) => {
  const normalized = normalizeSelectValue(value);
  if (normalized && !list.includes(normalized)) {
    list.push(normalized);
  }
};

const buildOptionAliases = (option, isGoogleService) => {
  const aliases = [];
  if (!option || typeof option !== 'object') {
    pushUniqueValue(aliases, option);
    return aliases;
  }
  if (isGoogleService) {
    pushUniqueValue(aliases, option.name);
    pushUniqueValue(aliases, option.label);
    pushUniqueValue(aliases, option.id);
    pushUniqueValue(aliases, option.resourceName);
    pushUniqueValue(aliases, option.value);
    pushUniqueValue(aliases, option.conversion);
    pushUniqueValue(aliases, option.action_type);
    return aliases;
  }
  pushUniqueValue(aliases, option.name);
  pushUniqueValue(aliases, option.label);
  pushUniqueValue(aliases, option.id);
  pushUniqueValue(aliases, option.value);
  pushUniqueValue(aliases, option.resourceName);
  pushUniqueValue(aliases, option.action_type);
  pushUniqueValue(aliases, option.conversion);
  return aliases;
};

const getCanonicalOptionValue = (option, isGoogleService) => {
  return buildOptionAliases(option, isGoogleService)[0] || '';
};

const ReportConversionSelect = (props) => {
  const {
    service = 'meta',
    cid: cidProp,
    sdate: sdateProp,
    edate: edateProp,
    accountId,
    gmcid,
    value,
    defaultValue = [],
    onChange,
    onSelectionChange,
    label = 'Conversions',
    placeholder = 'Select conversions',
    disabled = false,
    size = 'small',
    fullWidth = true,
  } = props;

  const { appVars } = useContext(AppVarsContext);

  const theme = useTheme();
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(() => (Array.isArray(value) ? value : defaultValue));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    sdate: sdateProp || sessionStorage.getItem('sDate'),
    edate: edateProp || sessionStorage.getItem('eDate'),
  });

  const isGoogleService = service === 'google' || service === 'adwords';
  const getOptionAliases = (option) => {
    return buildOptionAliases(option, isGoogleService);
  };

  const getOptionValue = (option) => {
    return getCanonicalOptionValue(option, isGoogleService);
  };

  const getOptionLabel = (option) => {
    if (!option) return '';
    if (isGoogleService) {
      return option.name || option.conversion || option.id || '';
    }
    return option.name || option.label || option.action_type || option.id || '';
  };

  useEffect(() => {
    if (Array.isArray(value)) {
      const normalized = value
        .map((entry) => (typeof entry === 'string' || typeof entry === 'number'
          ? normalizeSelectValue(entry)
          : getOptionValue(entry)))
        .filter(Boolean);
      setSelected(normalized);
    }
  }, [value, isGoogleService]);

  useEffect(() => {
    setDateRange({
      sdate: sdateProp || sessionStorage.getItem('sDate'),
      edate: edateProp || sessionStorage.getItem('eDate'),
    });
  }, [sdateProp, edateProp]);

  useEffect(() => {
    const handleRangeChange = () => {
      setDateRange({
        sdate: sdateProp || sessionStorage.getItem('sDate'),
        edate: edateProp || sessionStorage.getItem('eDate'),
      });
    };
    window.addEventListener('dc', handleRangeChange);
    return () => window.removeEventListener('dc', handleRangeChange);
  }, [sdateProp, edateProp]);

  useEffect(() => {
    const endpoint = SERVICE_ENDPOINTS[service];
    const cid = cidProp || sessionStorage.getItem('rid');
    const needsAccountId = service === 'google' || service === 'adwords';
    if (!endpoint || !cid || !dateRange.sdate || !dateRange.edate || !appVars?.api_url) {
      setOptions([]);
      return;
    }
    if (needsAccountId && !accountId) {
      setOptions([]);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError('');

    const resolvedAccountId = Array.isArray(accountId) ? accountId[0] : accountId;
    const aidValue = isGoogleService ? (resolvedAccountId || null) : null;

    fetch(`${appVars.api_url}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        cid,
        aid: aidValue,
        sdate: dateRange.sdate,
        edate: dateRange.edate,
        ...(resolvedAccountId ? { accountId: resolvedAccountId } : {}),
        ...(gmcid ? { gmcid } : {})
      }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data?.error || `Request failed (${res.status})`);
        }
        return data;
      })
      .then((data) => {
        if (!isMounted) return;
        const list = Array.isArray(data?.conversions) ? data.conversions : [];
        setOptions(list);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err?.message || String(err));
        setOptions([]);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [appVars?.api_url, service, cidProp, accountId, gmcid, dateRange.sdate, dateRange.edate]);

  const selectedOptions = useMemo(() => {
    if (!options.length || !selected.length) return [];
    const resolved = [];
    const seenValues = new Set();

    selected.forEach((entry) => {
      const target = normalizeSelectValue(entry);
      if (!target) return;

      const exactMatch = options.find((option) => getOptionValue(option) === target);
      const aliasMatches = exactMatch
        ? [exactMatch]
        : options.filter((option) => getOptionAliases(option).includes(target));

      if (aliasMatches.length !== 1) return;

      const match = aliasMatches[0];
      const canonical = getOptionValue(match);
      if (!canonical || seenValues.has(canonical)) return;
      seenValues.add(canonical);
      resolved.push(match);
    });

    return resolved;
  }, [options, selected, isGoogleService]);

  return (
    <Box sx={{ minWidth: 300, mr: 1 }}>
      <Autocomplete
        multiple
        size={size}
        fullWidth={fullWidth}
        sx={getExecutiveGlassAutocompleteSx(theme)}
        slotProps={{
          paper: {
            sx: getExecutiveGlassPopperSx(theme)
          }
        }}
        options={options}
        value={selectedOptions}
        loading={loading}
        disabled={disabled}
        isOptionEqualToValue={(option, val) => getOptionValue(option) === getOptionValue(val)}
        getOptionLabel={(option) => getOptionLabel(option)}
        onChange={(event, newValue) => {
          const next = Array.from(new Set(newValue.map((entry) => getOptionValue(entry)).filter(Boolean)));
          setSelected(next);
          if (onChange) onChange(next, newValue);
          if (onSelectionChange) onSelectionChange(next, newValue);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              size="small"
              key={getOptionValue(option) || index}
              label={getOptionLabel(option)}
              {...getTagProps({ index })}
              sx={{ maxWidth: '100%' }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={Boolean(error)}
            helperText={error ? `Unable to load conversions: ${error}` : undefined}
            sx={getExecutiveGlassAutocompleteSx(theme)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default ReportConversionSelect;
